/**
 * What staff do to accounts. hg-auth checks each by the caller's role and by
 * whom it is done to (nobody acts on their own account or on a superadmin;
 * an admin acts on clients, nurses and partners, not on staff): a refusal is
 * its `403`, shown where the action was tried.
 *
 * None takes an `Idempotency-Key`: each says what the account should be, and
 * saying it twice changes nothing.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { authApi, unwrap } from "@/shared/api/clients";
import { ProblemError } from "@/shared/api/problem";
import { meQueryOptions, type Me } from "@/shared/auth/me";

import { ACCOUNTS_KEY } from "./queries";
import type { Account, BlockReason, FoundClient, StaffRole, UpdateMe } from "./types";

const who = (userId: string) => ({ path: { user_id: userId } });

export interface Block {
  reason: BlockReason;
  /** What happened, for other staff. */
  note: string;
  /** Until when, as an instant; null: until somebody unblocks. */
  until: string | null;
}

/** Blocks an account: no sign-in anywhere, every device signed out. */
export async function blockAccount(userId: string, block: Block): Promise<void> {
  await authApi.POST("/v2/admin/users/{user_id}/block", {
    params: who(userId),
    body: { reason: block.reason, note: block.note === "" ? null : block.note, until: block.until },
  });
}

export async function unblockAccount(userId: string): Promise<void> {
  await authApi.POST("/v2/admin/users/{user_id}/unblock", { params: who(userId) });
}

/** Corrects a name: the only way one verified by passport changes. */
export async function changeName(
  userId: string,
  fullName: string,
  reason: string,
): Promise<Account> {
  const { data } = await unwrap(
    authApi.PATCH("/v2/admin/users/{user_id}/name", {
      params: who(userId),
      body: { full_name: fullName, reason },
    }),
  );
  return data;
}

/** Moves the account to another phone; every device is signed out. */
export async function changePhone(userId: string, phone: string, reason: string): Promise<Account> {
  const { data } = await unwrap(
    authApi.PATCH("/v2/admin/users/{user_id}/phone", {
      params: who(userId),
      body: { phone, reason },
    }),
  );
  return data;
}

/** Signs one of a person's devices out. */
export async function closeSession(userId: string, sessionId: string): Promise<void> {
  await authApi.DELETE("/v2/admin/users/{user_id}/sessions/{session_id}", {
    params: { path: { user_id: userId, session_id: sessionId } },
  });
}

/** Deletes the account, as its owner would. A superadmin's to do. */
export async function deleteAccount(userId: string): Promise<void> {
  await authApi.DELETE("/v2/admin/users/{user_id}", { params: who(userId) });
}

/** A temporary password, shown once: every device is signed out, and it must be replaced at the next sign-in. */
export async function resetPassword(userId: string): Promise<string> {
  const { data } = await unwrap(
    authApi.POST("/v2/admin/users/{user_id}/password-reset", { params: who(userId) }),
  );
  return data.temporary_password;
}

/** Who has this phone, without creating anyone. The phone travels in the body, never in an address. */
export async function lookupClient(phone: string): Promise<FoundClient> {
  const { data } = await unwrap(authApi.POST("/v2/admin/clients/lookup", { body: { phone } }));
  return data;
}

export interface PhoneBlockInput extends Block {
  phone: string;
}

/** Blocks a number: no code is sent to it and it cannot sign up. */
export async function blockPhone(block: PhoneBlockInput): Promise<void> {
  await authApi.POST("/v2/admin/phone-blocks", {
    body: {
      phone: block.phone,
      reason: block.reason,
      note: block.note === "" ? null : block.note,
      until: block.until,
    },
  });
}

export async function liftPhoneBlock(blockId: string): Promise<void> {
  await authApi.DELETE("/v2/admin/phone-blocks/{block_id}", {
    params: { path: { block_id: blockId } },
  });
}

export interface NewStaff {
  fullName: string;
  phone: string;
  username: string;
  role: StaffRole;
  areaIds: string[];
}

/** Creates a staff account, or makes an existing person staff. The password is shown once. */
export async function createStaff(
  staff: NewStaff,
): Promise<{ user: Account; temporaryPassword: string }> {
  const { data } = await unwrap(
    authApi.POST("/v2/admin/staff", {
      body: {
        full_name: staff.fullName,
        phone: staff.phone,
        username: staff.username,
        role: staff.role,
        // A superadmin is limited to no area: none is sent.
        ...(staff.role === "superadmin" ? {} : { area_ids: staff.areaIds }),
      },
    }),
  );
  return { user: data.user, temporaryPassword: data.temporary_password };
}

/** Gives the person this staff role in place of the one they had; their devices are signed out. */
export async function setStaffRole(userId: string, role: StaffRole): Promise<Account> {
  const { data } = await unwrap(
    authApi.PUT("/v2/admin/users/{user_id}/staff-role", { params: who(userId), body: { role } }),
  );
  return data;
}

/** Takes the staff role away. A client's or a nurse's role stays. */
export async function removeStaffRole(userId: string): Promise<void> {
  await authApi.DELETE("/v2/admin/users/{user_id}/staff-role", { params: who(userId) });
}

/** Sets the areas an admin or a dispatcher works in; their devices are signed out. */
export async function setStaffAreas(userId: string, areaIds: string[]): Promise<string[]> {
  const { data } = await unwrap(
    authApi.PUT("/v2/admin/users/{user_id}/areas", {
      params: who(userId),
      body: { area_ids: areaIds },
    }),
  );
  return data.area_ids;
}

// ---- one's own account ----

/** Changes one's own name or language: only what is sent changes. Answers the profile. */
export async function updateMe(change: UpdateMe): Promise<Me> {
  const { data } = await unwrap(authApi.PATCH("/v2/me", { body: change }));
  return data;
}

/** What hg-auth takes for a photo: a JPEG, PNG or WebP picture up to 5 MB. */
export const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/** Why a picture cannot be one's photo; null when it can. */
export function avatarProblem(file: File): "type" | "size" | null {
  if (!AVATAR_TYPES.some((each) => each === file.type)) return "type";
  return file.size === 0 || file.size > AVATAR_MAX_BYTES ? "size" : null;
}

/**
 * A new photo of one's own, in hg-auth's two steps: ask where to send it,
 * send the file there with the method and the headers given (the link fixes
 * the type and the exact size), then confirm its key. Answers the profile.
 */
export async function setMyAvatar(file: File): Promise<Me> {
  const type = AVATAR_TYPES.find((each) => each === file.type);
  if (type === undefined) {
    throw new ProblemError({ status: 422, code: "upload_invalid", title: "Not a picture" });
  }
  const { data: upload } = await unwrap(
    authApi.POST("/v2/me/avatar/uploads", { body: { content_type: type, size: file.size } }),
  );
  // The storage's own address: nothing of the app's goes with it.
  const sent = await fetch(
    new Request(upload.upload_url, { method: upload.method, headers: upload.headers, body: file }),
  );
  if (!sent.ok) {
    throw new ProblemError({ status: sent.status, code: "upload_failed", title: "Upload failed" });
  }
  const { data } = await unwrap(authApi.PUT("/v2/me/avatar", { body: { key: upload.key } }));
  return data;
}

/** Takes one's photo off: the file leaves the storage within minutes. Answers the profile. */
export async function removeMyAvatar(): Promise<Me> {
  const { data } = await unwrap(authApi.DELETE("/v2/me/avatar"));
  return data;
}

/** Changes one's own password; every other device is signed out, this one stays. */
export async function changeMyPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  // `204`: there is nothing to unwrap; a refusal throws on the way back.
  await authApi.POST("/v2/me/password", {
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

/** Signs one of one's own devices out. One signed out already is no error. */
export async function closeMySession(sessionId: string): Promise<void> {
  await authApi.DELETE("/v2/sessions/{session_id}", {
    params: { path: { session_id: sessionId } },
  });
}

/** Signs out every device of one's own but this one; answers how many were. */
export async function closeMyOtherSessions(): Promise<number> {
  const { data } = await unwrap(authApi.POST("/v2/sessions/revoke-others"));
  return data.revoked;
}

/**
 * A change to one's own account: when it is done, or refused, the profile
 * and the devices are read again.
 */
export function useMyAccountAction<Input, Result>(run: (input: Input) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => queryClient.invalidateQueries({ queryKey: meQueryOptions.queryKey }),
  });
}

/**
 * An action on an account. When it is done, or refused, everything read
 * about accounts is read again: a refusal often means the account is no
 * longer what the page showed.
 */
export function useAccountAction<Input, Result>(run: (input: Input) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}
